import { useState } from 'react';
import { X, Star, StarOff, Loader2, Search, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpdateCandidateSkill, useRemoveCandidateSkill, useAddCandidateSkill } from '../hooks';
import { useSkillSearch } from '../hooks/useSkills';
import type { CandidateSkill } from '../types';

interface SkillsManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: number;
  skills: CandidateSkill[];
}

const MAX_PRIMARY_SKILLS = 3;

export function SkillsManagementDialog({
  open,
  onOpenChange,
  candidateId,
  skills,
}: SkillsManagementDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const updateSkill = useUpdateCandidateSkill();
  const removeSkill = useRemoveCandidateSkill();
  const addSkill = useAddCandidateSkill();
  const { data: searchResults, isLoading: isSearching } = useSkillSearch(searchQuery);

  const primarySkills = skills.filter((s) => s.isPrimary);
  const canAddPrimary = primarySkills.length < MAX_PRIMARY_SKILLS;

  const handleTogglePrimary = async (skill: CandidateSkill) => {
    if (skill.isPrimary) {
      await updateSkill.mutateAsync({
        candidateId,
        skillId: skill.skillId,
        dto: { isPrimary: false },
      });
    } else if (canAddPrimary) {
      await updateSkill.mutateAsync({
        candidateId,
        skillId: skill.skillId,
        dto: { isPrimary: true },
      });
    }
  };

  const handleRemoveSkill = async (skill: CandidateSkill) => {
    await removeSkill.mutateAsync({
      candidateId,
      skillId: skill.skillId,
    });
  };

  const handleAddSkill = async (skillId: number) => {
    await addSkill.mutateAsync({
      candidateId,
      dto: { skillId, isPrimary: false },
    });
    setSearchQuery('');
    setIsAdding(false);
  };

  const existingSkillIds = new Set(skills.map((s) => s.skillId));
  const availableSkills = searchResults?.filter((s) => !existingSkillIds.has(s.id)) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Gestion des competences</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Primary skills info */}
          <div className="text-sm text-muted-foreground">
            Competences principales: {primarySkills.length}/{MAX_PRIMARY_SKILLS}
          </div>

          {/* Skills list */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {skills.map((skill) => (
              <div
                key={skill.id}
                className="flex items-center justify-between p-2 rounded-lg border bg-card"
              >
                <div className="flex items-center gap-2">
                  <Badge variant={skill.isPrimary ? 'default' : 'secondary'}>
                    {skill.skillName}
                  </Badge>
                  {skill.level > 0 && (
                    <span className="text-xs text-muted-foreground">
                      Niveau {skill.level}/5
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleTogglePrimary(skill)}
                    disabled={!skill.isPrimary && !canAddPrimary || updateSkill.isPending}
                    title={skill.isPrimary ? 'Retirer des principales' : 'Definir comme principale'}
                  >
                    {updateSkill.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : skill.isPrimary ? (
                      <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                    ) : (
                      <StarOff className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => handleRemoveSkill(skill)}
                    disabled={removeSkill.isPending}
                    title="Supprimer"
                  >
                    {removeSkill.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
            {skills.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucune competence
              </p>
            )}
          </div>

          {/* Add skill section */}
          <div className="border-t pt-4">
            {isAdding ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher une competence..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                    autoFocus
                  />
                </div>
                {isSearching && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                )}
                {availableSkills.length > 0 && (
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {availableSkills.map((skill) => (
                      <Badge
                        key={skill.id}
                        variant="outline"
                        className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                        onClick={() => handleAddSkill(skill.id)}
                      >
                        + {skill.name}
                      </Badge>
                    ))}
                  </div>
                )}
                {searchQuery.length >= 2 && !isSearching && availableSkills.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center">
                    Aucune competence trouvee
                  </p>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsAdding(false);
                    setSearchQuery('');
                  }}
                >
                  Annuler
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAdding(true)}
                className="w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Ajouter une competence
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
